import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

interface JobPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  description: string;
  requirements: string[];
  isExpanded: boolean;
}

const JoinUs: React.FC = () => {
  // Initial job listings
  const initialJobs: JobPosition[] = [
    {
      id: '1',
      title: 'Senior AI Engineer - Medical NLP',
      department: 'Engineering',
      location: 'Toronto, Canada (Remote Eligible)',
      description: 
        'We\'re looking for a Senior AI Engineer specializing in Natural Language Processing (NLP) to help us build and improve our medical text understanding systems. You\'ll work on developing cutting-edge deep learning models that can extract valuable insights from complex medical documents, enabling our AI assistant to better understand and respond to healthcare professionals\' needs.',
      requirements: [
        'PhD or MSc in Computer Science, AI, NLP, or related field',
        '5+ years of experience in NLP and deep learning, with demonstrable projects',
        'Strong background in transformer-based language models (BERT, GPT, etc.)',
        'Experience with healthcare or medical NLP is highly preferred',
        'Proficiency in Python and modern ML frameworks (PyTorch, TensorFlow)',
        'Excellent understanding of software engineering best practices',
        'Strong communication skills with ability to explain complex concepts clearly',
        'Ability to work in a fast-paced, collaborative environment'
      ],
      isExpanded: false
    },
    {
      id: '2',
      title: 'Medical Knowledge Engineer',
      department: 'Medical Content',
      location: 'Hong Kong or Toronto (Remote Eligible)',
      description: 
        'Join our multidisciplinary team as a Medical Knowledge Engineer to help build, maintain, and expand our medical knowledge base. Your expertise will inform our AI models by providing domain-specific medical knowledge structure and validation. This role bridges the gap between clinical understanding and AI technology, ensuring our solutions are medically accurate and useful in real healthcare settings.',
      requirements: [
        'MD or equivalent medical degree, with clinical experience',
        'Experience in knowledge representation, ontologies, or medical informatics',
        'Understanding of electronic health records and healthcare workflows',
        'Interest in AI and technology applications in healthcare',
        'Excellent attention to detail and analytical thinking',
        'Strong communication skills and ability to work with technical teams',
        'Experience in research or academic medicine is a plus',
        'Knowledge of multiple languages (especially Chinese and English) is highly valued'
      ],
      isExpanded: false
    }
  ];

  const [jobs, setJobs] = useState<JobPosition[]>(initialJobs);

  // Toggle job details
  const toggleJobExpansion = (id: string) => {
    setJobs(jobs.map(job => 
      job.id === id ? { ...job, isExpanded: !job.isExpanded } : job
    ));
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <Header />

      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="absolute inset-0">
          <img
            className="w-full h-full object-cover mix-blend-multiply filter brightness-75"
            src="https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1950&q=80"
            alt="Team working"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Join Our Team</h1>
          <p className="mt-6 max-w-3xl text-xl text-cyan-100">
            Help us build the future of AI-powered healthcare. We're a diverse team united by our passion for innovation and making a real difference in patients' lives.
          </p>
        </div>
      </div>

      {/* Culture section */}
      <div className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Our Culture</h2>
            <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
              Work with purpose
            </p>
            <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
              At Agilis AI, we're building technology that transforms healthcare delivery around the world.
            </p>
          </div>

          <div className="mt-20">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                    <Icon iconName="Diversity1" size={24} />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Global & Diverse</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Our team spans continents and cultures, bringing together diverse perspectives that drive innovation. We believe the best solutions come from teams with varied backgrounds and experiences.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                    <Icon iconName="EmojiObjects" size={24} />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Innovation Mindset</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  We encourage bold thinking and creative problem-solving. Our team members have the freedom to explore new ideas and approaches, with the resources to bring them to life.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                    <Icon iconName="Psychology" size={24} />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Interdisciplinary Collaboration</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Engineers work alongside clinicians, designers collaborate with data scientists. This interdisciplinary approach ensures our technology is both cutting-edge and clinically relevant.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                    <Icon iconName="Escalator" size={24} />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Growth & Development</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  We invest in our team's professional growth through mentorship, learning opportunities, and challenging projects that push boundaries and develop new skills.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Benefits section */}
      <div className="bg-gray-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Benefits</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Why join Agilis AI?
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              We believe in taking care of our team with competitive compensation and comprehensive benefits.
            </p>
          </div>

          <div className="mt-16">
            <div className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-12 lg:grid-cols-3 lg:gap-x-8">
              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="HealthAndSafety" size={24} className="mr-2 text-indigo-500" />
                  Comprehensive Health Coverage
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Full medical, dental, and vision insurance for you and your dependents with global coverage options.
                </p>
              </div>

              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="FlightTakeoff" size={24} className="mr-2 text-indigo-500" />
                  Flexible Work Arrangements
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Remote-friendly culture with flexible hours and location-independent work options.
                </p>
              </div>

              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="EventAvailable" size={24} className="mr-2 text-indigo-500" />
                  Generous Time Off
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Unlimited PTO policy plus paid parental leave to ensure you maintain work-life balance.
                </p>
              </div>

              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="School" size={24} className="mr-2 text-indigo-500" />
                  Learning & Development
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Professional development budget for conferences, courses, and learning resources.
                </p>
              </div>

              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="MonetizationOn" size={24} className="mr-2 text-indigo-500" />
                  Competitive Compensation
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Competitive salary with equity options to share in the company's success.
                </p>
              </div>

              <div className="relative">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <Icon iconName="Diversity2" size={24} className="mr-2 text-indigo-500" />
                  Inclusive Workplace
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Commitment to diversity, equity, and inclusion with regular team events and activities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Open positions */}
      <div className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Careers</h2>
            <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
              Open Positions
            </p>
            <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
              Join our team of passionate professionals building the future of healthcare AI.
            </p>
          </div>

          <div className="mt-12">
            <div className="space-y-4">
              {jobs.map((job) => (
                <div 
                  key={job.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleJobExpansion(job.id)}
                    className="w-full text-left px-6 py-5 bg-white flex justify-between items-center"
                  >
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{job.title}</h3>
                      <div className="mt-1 flex items-center space-x-4">
                        <div className="text-sm text-gray-500 flex items-center">
                          <Icon iconName="Business" size={16} className="mr-1" />
                          {job.department}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Icon iconName="LocationOn" size={16} className="mr-1" />
                          {job.location}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Icon 
                        iconName={job.isExpanded ? "ExpandLess" : "ExpandMore"} 
                        size={24} 
                        className="text-gray-400" 
                      />
                    </div>
                  </button>
                  
                  {job.isExpanded && (
                    <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                      <div className="prose max-w-none">
                        <p>{job.description}</p>
                        <h4 className="text-base font-medium text-gray-900 mt-4">Requirements:</h4>
                        <ul className="mt-2 space-y-1">
                          {job.requirements.map((req, index) => (
                            <li key={index} className="text-gray-700">{req}</li>
                          ))}
                        </ul>
                        <div className="mt-6">
                          <button
                            type="button"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Apply Now
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Don't see a fit section */}
          <div className="mt-12 text-center">
            <h3 className="text-lg font-medium text-gray-900">Don't see a position that fits your skills?</h3>
            <p className="mt-2 text-base text-gray-500">
              We're always looking for talented individuals to join our team. Send us your resume and tell us how you can contribute.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gradient-to-r from-indigo-700 to-blue-700 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-indigo-200 tracking-wide uppercase">Testimonials</h2>
            <p className="mt-1 text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight">
              Hear from our team
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <img
                  className="h-12 w-12 rounded-full object-cover mr-4"
                  src="https://images.unsplash.com/photo-1573497019236-61f323342eb4?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Employee 1"
                />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Emily Chen</h4>
                  <p className="text-sm text-gray-500">AI Engineer, Toronto</p>
                </div>
              </div>
              <p className="text-gray-700">
                "The collaborative environment at Agilis AI is unlike anywhere I've worked before. I get to solve complex problems alongside brilliant colleagues from diverse backgrounds, all while knowing our work directly impacts patient care."
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <img
                  className="h-12 w-12 rounded-full object-cover mr-4"
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Employee 2"
                />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">David Kim</h4>
                  <p className="text-sm text-gray-500">Medical Content Lead, Remote</p>
                </div>
              </div>
              <p className="text-gray-700">
                "As a physician, I wanted to impact healthcare beyond individual patient interactions. At Agilis AI, I'm able to leverage my clinical expertise to build AI systems that can potentially help millions of patients worldwide."
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <img
                  className="h-12 w-12 rounded-full object-cover mr-4"
                  src="https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  alt="Employee 3"
                />
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Sophia Wong</h4>
                  <p className="text-sm text-gray-500">Product Manager, Hong Kong</p>
                </div>
              </div>
              <p className="text-gray-700">
                "The flexible work culture allows me to collaborate with team members across different time zones while maintaining work-life balance. I appreciate how Agilis AI values results over rigid schedules."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default JoinUs; 